import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-accent text-text-inverted shadow-[0_4px_14px_-4px_color-mix(in_oklab,var(--accent)_60%,transparent)] hover:opacity-90 active:scale-[0.98]',
        secondary: 'bg-surface text-text border border-border hover:border-text/40',
        ghost: 'bg-transparent text-text hover:bg-surface',
        outline: 'bg-transparent text-text border border-border hover:bg-surface',
        link: 'bg-transparent text-accent underline-offset-4 hover:underline',
        destructive: 'bg-error text-text-inverted hover:opacity-90 active:scale-[0.98]',
      },
      size: {
        sm: 'h-9 px-4 text-sm rounded-[var(--radius-button)]',
        md: 'h-10 px-5 text-sm rounded-[var(--radius-button)]',
        lg: 'h-12 px-7 text-base rounded-[var(--radius-button)]',
        icon: 'size-9 rounded-[var(--radius-button)]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { buttonVariants };
